<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import aiReviewsAPI from 'dashboard/api/aiReviews';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import PageHeader from '../components/PageHeader.vue';
import StatusBadge from '../components/StatusBadge.vue';
import { formatDateTime, getApiErrorMessage } from '../utils';

const { t } = useI18n();
const router = useRouter();

const policies = ref([]);
const isLoading = ref(false);
const isCreating = ref(false);
const showCreateForm = ref(false);
const form = ref({ name: '', description: '' });

const loadPolicies = async () => {
  try {
    isLoading.value = true;
    const { data } = await aiReviewsAPI.getReviewPolicies();
    const details = await Promise.all(
      (data.payload || []).map(async policy => {
        const response = await aiReviewsAPI.getReviewPolicy(policy.id);
        return response.data;
      })
    );
    policies.value = details;
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isLoading.value = false;
  }
};

const createPolicy = async () => {
  try {
    isCreating.value = true;
    const { data } = await aiReviewsAPI.createReviewPolicy(form.value);
    useAlert(t('AI_REVIEWS.POLICIES.CREATE_SUCCESS'));
    await router.push({
      name: 'ai_review_policy_show',
      params: { policyId: data.id },
    });
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isCreating.value = false;
  }
};

onMounted(loadPolicies);
</script>

<template>
  <PageHeader
    :title="t('AI_REVIEWS.POLICIES.TITLE')"
    :description="t('AI_REVIEWS.POLICIES.DESCRIPTION')"
  >
    <template #actions>
      <Button
        :label="t('AI_REVIEWS.POLICIES.NEW')"
        icon="i-lucide-plus"
        :disabled="showCreateForm"
        @click="showCreateForm = true"
      />
    </template>
  </PageHeader>

  <form
    v-if="showCreateForm"
    class="grid gap-4 p-5 mb-6 border rounded-xl border-n-weak bg-n-solid-2 md:grid-cols-2"
    @submit.prevent="createPolicy"
  >
    <Input
      id="ai-review-policy-name"
      v-model="form.name"
      :label="t('AI_REVIEWS.POLICIES.NAME')"
      required
    />
    <Textarea
      id="ai-review-policy-description"
      v-model="form.description"
      :label="t('AI_REVIEWS.POLICIES.DESCRIPTION_LABEL')"
      resize
    />
    <div class="flex justify-end gap-2 md:col-span-2">
      <Button
        type="button"
        color="slate"
        variant="faded"
        :label="t('AI_REVIEWS.COMMON.CANCEL')"
        @click="showCreateForm = false"
      />
      <Button
        type="submit"
        :label="t('AI_REVIEWS.COMMON.CREATE')"
        :is-loading="isCreating"
        :disabled="!form.name.trim() || isCreating"
      />
    </div>
  </form>

  <div v-if="isLoading" class="p-10 text-sm text-center text-n-slate-11">
    {{ t('AI_REVIEWS.COMMON.LOADING') }}
  </div>
  <div
    v-else-if="!policies.length"
    class="p-10 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
  >
    {{ t('AI_REVIEWS.POLICIES.EMPTY') }}
  </div>
  <div v-else class="overflow-x-auto border rounded-xl border-n-weak">
    <table class="w-full min-w-[760px] text-sm">
      <thead class="text-left bg-n-alpha-1 text-n-slate-11">
        <tr>
          <th class="p-3 font-medium">{{ t('AI_REVIEWS.POLICIES.NAME') }}</th>
          <th class="p-3 font-medium">
            {{ t('AI_REVIEWS.POLICIES.ACTIVE_VERSION') }}
          </th>
          <th class="p-3 font-medium">
            {{ t('AI_REVIEWS.STATUS.DRAFT') }}
          </th>
          <th class="p-3 font-medium">
            {{ t('AI_REVIEWS.COMMON.LAST_UPDATED') }}
          </th>
          <th class="p-3 font-medium" />
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak">
        <tr v-for="policy in policies" :key="policy.id">
          <td class="p-3">
            <p class="mb-1 font-medium text-n-slate-12">{{ policy.name }}</p>
            <p class="max-w-md mb-0 text-xs text-n-slate-11">
              {{ policy.description || '—' }}
            </p>
          </td>
          <td class="p-3">
            <span v-if="policy.active_version" class="flex items-center gap-2">
              <StatusBadge status="published" />
              <span>{{
                t('AI_REVIEWS.COMMON.VERSION', {
                  version: policy.active_version.version_number,
                })
              }}</span>
            </span>
            <span v-else class="text-n-slate-11">
              {{ t('AI_REVIEWS.POLICIES.NO_ACTIVE_VERSION') }}
            </span>
          </td>
          <td class="p-3 text-n-slate-11">
            {{
              policy.versions.some(version => version.state === 'draft')
                ? t('AI_REVIEWS.POLICIES.DRAFT_AVAILABLE')
                : t('AI_REVIEWS.POLICIES.NO_DRAFT')
            }}
          </td>
          <td class="p-3 text-n-slate-11">
            {{ formatDateTime(policy.updated_at) }}
          </td>
          <td class="p-3 text-right">
            <RouterLink
              :to="{
                name: 'ai_review_policy_show',
                params: { policyId: policy.id },
              }"
            >
              <Button
                :label="t('AI_REVIEWS.COMMON.OPEN')"
                color="slate"
                variant="faded"
                size="sm"
              />
            </RouterLink>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
